import { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import Spinner from 'components/general/Spinner';

import { useActions } from 'utils/hooks';
import * as indexesActions from 'store/market-indexes/actions';
import SingleBulkValue from './SingleBulkValue';
import moment from 'moment';
import { strToNumber } from 'common/utils/numbers';
import { transformBulkValues } from './helpers';
import { selectIndexesValues, selectBulkValues } from 'store/market-indexes/selectors';

const EditBulkValuesForm = ({ isOpen, selected, drawer, refetchData, setDeleting }) => {
  const [loading, setLoading] = useState(false);
  const [meta, setMeta] = useState(null);
  const [disabled, setDisabled] = useState(false);
  const indexes = useSelector(selectIndexesValues);
  const bulkValues = useSelector(selectBulkValues);

  const [isFetching, setIsFetching] = useState(false);

  const scrollRef = useRef(null);

  const [newValues, setNewValues] = useState(selected.map(el => null));
  const [newDate, setNewDate] = useState(moment().format('YYYY-MM-DD'));
  const [dateError, setDateError] = useState(null);
  const [valueError, setValueError] = useState(null);

  const [editDate, setEditDate] = useState(null);
  const [activeEditIndex, setActiveIndex] = useState(null);
  const [activeEdit, setActiveEdit] = useState(null);

  const [getIndexesValues, updateIndexValue, deleteIndexValue, setBulkValues] = useActions([
    indexesActions.getIndexesValues,
    indexesActions.updateIndexValue,
    indexesActions.deleteIndexValue,
    indexesActions.setBulkValues
  ]);

  useEffect(() => {
    if (indexes && selected) {
      setBulkValues(transformBulkValues(indexes, selected));
    }
  }, [selected, indexes]);

  const load = async currentPage => {
    const market_index_id = selected.map(el => el.id);
    const data = await getIndexesValues({
      market_index_id,
      per_page: 30,
      current_page: currentPage || 1
    });

    if (!data) {
      setIsFetching(false);
      return;
    }

    setMeta({ current_page: data.current_page, last_page: data.last_page, total: data.total });

    setLoading(false);
  };

  useEffect(() => {
    setNewDate(moment().format('YYYY-MM-DD'));
    if (!isOpen) return;
    setActiveIndex(null);
    setActiveEdit(null);
    setLoading(true);
    load();
  }, [isOpen]);

  const onNumberInputChange = (value, i) => {
    let newArr = [...newValues];
    newArr[i] = value;
    setNewValues(newArr);
  };

  const onDateChange = date => {
    setDateError(null);
    setNewDate(date);
  };

  const onEditDateChange = date => {
    setEditDate(date);
  };

  const onEditNumberInputChange = (value, i) => {
    let newArr = [...activeEdit];
    newArr[i] = value;
    setActiveEdit(newArr);
  };

  const onAddNewIndex = async () => {
    if (!newDate) {
      setDateError('Please provide a date');
      return;
    }

    if (newValues.every(val => val === null || !val)) {
      setValueError('Please provide at least one value');
      return;
    }

    let nonEmptyValues = [];
    newValues.forEach((element, index) => {
      if (element) {
        nonEmptyValues.push({
          market_index_id: selected[index].id,
          value: strToNumber(element)
        });
      }
    });
    const params = {
      date: moment(newDate).format('YYYY-MM-DD'),
      index: nonEmptyValues
    };
    await updateIndexValue(params);
    setNewDate(moment().format('YYYY-MM-DD'));
    setNewValues(selected.map(el => null));
    load();
  };

  const onEditAddClick = async () => {
    if (!editDate) {
      setDateError('Please provide a date');
      return;
    }

    if (activeEdit.every(val => val === null || !val)) {
      setValueError('Please provide at least one value');
      return;
    }

    let nonEmptyValues = [];
    activeEdit.forEach((element, index) => {
      if (element) {
        nonEmptyValues.push({
          market_index_id: selected[index].id,
          value: strToNumber(element)
        });
      }
    });
    const params = {
      date: moment(editDate).format('YYYY-MM-DD'),
      index: nonEmptyValues
    };
    await updateIndexValue(params);
    setActiveIndex(null);
    setEditDate(null);
    setActiveEdit(null);
  };

  const deleteValues = () => {
    setDisabled(true);
    const marketIndexes = selected.map(sel => sel.id);
    const params = {
      date: moment(editDate).format('YYYY-MM-DD'),
      market_index_ids: marketIndexes
    };

    deleteIndexValue(params).then(response => {
      if (response) {
        setActiveIndex(null);
        setEditDate(null);
        setActiveEdit(null);
        setDisabled(false);
      }
    });

    // setDeleting(false);
  };

  const loadMore = async () => {
    const { clientHeight, scrollTop, scrollHeight } = scrollRef.current;
    const paddingOffset = 0.5;

    if (scrollHeight === scrollTop + clientHeight + paddingOffset) {
      setIsFetching(true);
      const market_index_id = selected.map(el => el.id);
      const data = await getIndexesValues({
        market_index_id,
        per_page: 30,
        current_page: meta.current_page + 1,
        loadMore: true
      });

      if (!data) {
        setIsFetching(false);
        return;
      }

      setMeta({ current_page: data.current_page, last_page: data.last_page, total: data.total });

      setIsFetching(false);
    }
  };

  useEffect(() => {
    if (!loading) {
      const tree = scrollRef.current;

      if (meta?.total > 1 && meta?.current_page < meta?.last_page && !isFetching) {
        tree?.addEventListener('scroll', loadMore, true);
      }

      return () => (meta?.total > 1 ? tree?.removeEventListener('scroll', loadMore, true) : null);
    }
  }, [meta?.last_page, loading, isFetching]);

  return loading ? (
    <Spinner className="ms-3 mt-2" />
  ) : (
    <div
      ref={scrollRef}
      className="edit-bulk-index-values dark-blue-scrollbar  pt-3 ps-4 pe-7 pb-4"
    >
      <div className="d-flex justify-content-start align-items-center mb-2">
        <div className="date-cell text-primary fw-bold fs-12">DATE</div>

        {selected.map(s => (
          <div
            key={s.id}
            className="index-cell  index-name text-primary fw-bold fs-12 text-uppercase "
          >
            {s.name}
          </div>
        ))}
      </div>
      <SingleBulkValue
        mode="add"
        date={newDate}
        onDateChange={onDateChange}
        onNumberInputChange={onNumberInputChange}
        values={newValues}
        dateError={!activeEdit ? dateError : ''}
        valueError={!activeEdit ? valueError : ''}
        onAddClick={onAddNewIndex}
        selected={selected}
        className={activeEdit ? 'disabled' : ''}
      />
      {Object.keys(bulkValues).map((value, i) => (
        <SingleBulkValue
          key={value}
          mode={activeEditIndex === i ? 'edit' : 'display'}
          date={editDate}
          onDateChange={onEditDateChange}
          onNumberInputChange={onEditNumberInputChange}
          values={activeEdit}
          dateError={activeEditIndex === i ? dateError : ''}
          valueError={activeEditIndex === i ? valueError : ''}
          selected={selected}
          displayDate={value}
          onAddClick={onEditAddClick}
          items={bulkValues && bulkValues[value] ? bulkValues[value] : null}
          onEditClick={() => {
            setActiveIndex(i);
            setEditDate(value);
            setActiveEdit(bulkValues[value]);
          }}
          onDeleteClick={deleteValues}
          setDeleting={setDeleting}
          disabled={disabled}
        />
      ))}
      {isFetching && (
        <div className="d-flex justify-content-center align-items-center">
          <Spinner className="ms-3 mt-2" />
        </div>
      )}
    </div>
  );
};

export default EditBulkValuesForm;
